import { getCurrentOrg } from "#webapp/src/util/org.util";
import { useMutation, useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { IAutopilotSuggestionOptions } from "@clockwise/client-commons/src/util/event-tag";
import { Day, DayOffsetTimeSlot, PreRoomFlexibility } from "@clockwise/schema";
import { Nav } from "@clockwise/web-commons/src/components/nav";
import { defaultDayOnOffMap } from "@clockwise/web-commons/src/ui/working-hours";
import { useIsOutlook } from "@clockwise/web-commons/src/util/OutlookContext";
import { Tooltip } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { isEmpty, keys } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { throttle } from "throttle-debounce";
import { IOrg, IViewer } from "../../__schema__";
import { setMenuDrawerOpen } from "../../state/actions/chrome-extension.actions";
import { IReduxState } from "../../state/reducers/root.reducer";
import { EFlex } from "../../util/event-tag.util";
import { getPreEventFlexibility, maybeGetWarningFromDomains } from "../../util/event.util";
import { logger } from "../../util/logger.util";
import {
  combineRequiredAndOptionalAttendees,
  flexTooltipMessage,
  getOutlookEventKey,
  useOutlookNewEventData,
} from "../../util/outlook.util";
import { AutopilotFormSidebarScheduler } from "../autopilot-form";
import { ChromeMenuDrawer } from "../chrome-menu-drawer";
import { OutlookMetadataQueryDocument } from "../outlook-event/__generated__/OutlookMetadata.generated";
import { RouteRenderer } from "../route-renderer";
import { PreSetEventFlexibilityDocument } from "./__generated__/OutlookNewEvent.generated";

export function OutlookNewEvent() {
  const isOutlook = useIsOutlook();
  const dispatch = useDispatch();

  const menuDrawerOpen = useSelector((state: IReduxState) => state.webExtension.menuDrawerOpen);

  // Queries and mutations
  const { data, loading: metadataLoading, error } = useQuery(OutlookMetadataQueryDocument);
  const [setEventFlex] = useMutation(PreSetEventFlexibilityDocument);

  const viewer = getValue(data?.viewer, "Viewer");
  const org = getCurrentOrg(viewer);
  const calendarId = org?.primaryOrgCalendar;

  // Outlook fields
  const {
    startTime,
    endTime,
    requiredAttendees,
    optionalAttendees,
    calendarIds,
  } = useOutlookNewEventData(calendarId);

  // Flexible meeting fields
  const [flex, setFlex] = React.useState(EFlex.Pin);
  const [dayOnOffMap, setDayOnOffMap] = React.useState(defaultDayOnOffMap);
  const [allowedStart, setAllowedStart] = React.useState<DayOffsetTimeSlot | null>(null);
  const [allowedEnd, setAllowedEnd] = React.useState<DayOffsetTimeSlot | null>(null);

  const maybeWarning = org?.domains
    ? maybeGetWarningFromDomains(org.domains, calendarIds)
    : undefined;

  const onAutopilotChange = useCallback(
    ({
      flex,
      timeRange,
      daysOfWeek,
    }: IAutopilotSuggestionOptions & {
      daysOfWeek?: Day[];
    }) => {
      setFlex(flex);
      setAllowedStart(timeRange?.allowedStart ?? null);
      setAllowedEnd(timeRange?.allowedEnd ?? null);

      if (!isEmpty(daysOfWeek)) {
        const newDaysOnOffMap = {
          ...dayOnOffMap,
        };
        keys(newDaysOnOffMap).forEach((day) => {
          newDaysOnOffMap[day as Day] = false;
        });
        daysOfWeek?.forEach((day) => {
          newDaysOnOffMap[day] = true;
        });

        setDayOnOffMap(newDaysOnOffMap);
      }
    },
    [dayOnOffMap],
  );

  // Throttle setEventFlex to allow us to use it below in the useEffect.
  const setEventFlexThrottled = useMemo(() => throttle(350, setEventFlex, { noLeading: true }), [
    setEventFlex,
  ]);

  // Until we can tap into the create event button, fire setEventFlex off every time flex stuff, start, end, or attendees change.
  useEffect(() => {
    if (!org?.id || !startTime || !endTime) {
      if (flex !== EFlex.Pin) {
        logger.error("Missing org, start or end time unable to set flex on new Outlook event");
      }
      return;
    }

    const preEventFlexibility = getPreEventFlexibility(
      flex,
      dayOnOffMap,
      allowedStart,
      allowedEnd,
      PreRoomFlexibility.KeepCurrentRoom, // TODO support room flex when we have M365 room support.
    );
    const attendees = combineRequiredAndOptionalAttendees(requiredAttendees, optionalAttendees);
    const preEventKey = getOutlookEventKey(startTime, endTime, attendees);

    void setEventFlexThrottled({
      variables: {
        input: {
          orgRelayId: org?.id,
          calendarId: calendarId,
          preEventKey,
          preEventFlexibility,
        },
      },
    });
  }, [
    startTime,
    endTime,
    allowedEnd,
    allowedStart,
    optionalAttendees,
    requiredAttendees,
    flex,
    dayOnOffMap,
    org?.id,
    setEventFlexThrottled,
    calendarId,
  ]);

  const onClickNavMenu = useCallback(() => dispatch(setMenuDrawerOpen(true)), [dispatch]);

  return (
    <RouteRenderer loading={metadataLoading} error={!isOutlook || !!error}>
      <>
        <ChromeMenuDrawer viewer={viewer as IViewer} open={menuDrawerOpen} style={{ zIndex: 30 }} />
        <Nav
          showMenu={true}
          showCollapse={false}
          onClickCollapse={() => null}
          onClickMenu={onClickNavMenu}
        />
        <div className="cw-mb-2 cw-text-sm cw-font-bold cw-text-positive cw-flex cw-flex-row cw-items-center cw-m-4">
          <h1 className="cw-heading-xl cw-p-0 cw-m-0 cw-mr-[10px]">Meeting Flexibility</h1>
          <Tooltip placement="bottom" title={flexTooltipMessage} className="cw-max-w-sm">
            <InfoOutlined className="cw-h-4 cw-w-4 cw-mb-0.5 cw-text-info" />
          </Tooltip>
        </div>
        <div className="cw-m-2 cw-mt-4">
          <AutopilotFormSidebarScheduler
            org={org as IOrg}
            selectedDate={startTime?.toDateString?.() || new Date().toDateString()}
            showRoom={false} // We dont support yet
            flex={flex}
            allowSimilarRooms={false}
            timeRange={{
              allowedStart: allowedStart,
              allowedEnd: allowedEnd,
            }}
            dayOnOffMap={dayOnOffMap}
            onChange={onAutopilotChange}
            warning={maybeWarning}
          />
        </div>
      </>
    </RouteRenderer>
  );
}
